import { SDKResponse } from '@commercetools/frontend-sdk';
import { WithSDKError } from 'shared/types/shared';

const CT_INFRA_ERROR_MESSAGES = [
  'Failed to fetch',
  'Load failed',
  'Temporary overloading',
  'NetworkError',
  'socket hang up',
  'SQLSTATE',
  'upstream connect error or disconnect/reset before headers',
  'FetchEvent.respondWith received an error',
];

/**
 * Makes a Commercetools API call with retry logic in case of failure
 *
 * This function makes an API call with a total of 4 attempts.
 * One initial call and up to 3 retries if the call fails.
 * Each retry is separated by an linearly increasing backoff period,
 * starting at 1000ms and increasing by 1000ms with each subsequent attempt.
 */
export const makeSDKCallWithRetries = async <T extends WithSDKError>(
  actionName: string,
  customerId: string,
  fn: () => Promise<SDKResponse<T>>,
  totalRetries = 3,
  baseBackoff = 1000,
): Promise<SDKResponse<T>> => {
  const attempt = async (attemptNumber: number): Promise<SDKResponse<T>> => {
    const response = await fn();

    const error = response.isError
      ? response.error
      : response.data.isError
        ? response.data.error
        : null;

    const hasCtInfraError =
      !!error &&
      CT_INFRA_ERROR_MESSAGES.some((message) =>
        error.message?.includes(message),
      );

    const isExtensionTimeoutError =
      !!error &&
      !error.code &&
      ['cart/prepareCart', 'cart/prepareCartForProgram'].includes(actionName) &&
      /^Error: Calling extension action-cart-.* failed\.$/.test(
        error.message || '',
      );

    const errorCode = (error?.code || error?.name) as string;
    const isConcurrentModificationForPrepCart =
      ['cart/prepareCart', 'cart/prepareCartForProgram'].includes(actionName) &&
      !!error &&
      errorCode === 'ConcurrentModification';

    if (
      !hasCtInfraError &&
      !isConcurrentModificationForPrepCart &&
      !isExtensionTimeoutError
    ) {
      return response;
    }

    const nextAttempt = attemptNumber + 1;
    const nextBackoff = baseBackoff * nextAttempt;

    if (attemptNumber >= totalRetries) {
      console.error(
        `Exceeded retries attempt for ${actionName} for customer: ${customerId} with error:`,
        error,
      );
      return response;
    }

    console.warn(
      `${actionName} for customer: ${customerId} failed with error: ${errorCode}: ${error.message}. Retrying attempt #${nextAttempt} in ${nextBackoff} ms...`,
    );

    await new Promise((res) => {
      setTimeout(res, nextBackoff);
    });

    return attempt(nextAttempt);
  };

  return attempt(0);
};
